import { useState, useMemo } from 'react';
import { Collapse, Group, Text, Stack } from '@mantine/core';
import dayjs from 'dayjs';
import { addWorkdays, calendarDaysBetween, fromISODate } from './workdays';
import type { QuotationSchedule } from './NewQuotationModal';
import { IconGantt } from '../../components/icons/CncIcons';

interface Bar {
  label: string;
  color: string;
  start: Date;
  end: Date;
  milestone: boolean; // 0-day step → rendered as a diamond
  // Canonical ORDER_STEPS name this bar represents. Used to match currentStep from the
  // Ongoing Order screen so we can highlight the right row.
  step: 'Order Started' | 'Supplying Consumables' | 'In Execution' | 'Preparing to Ship' | 'Shipped' | 'Delivered';
}

function computeBars(s: QuotationSchedule, includeDelivered: boolean): Bar[] {
  const start = fromISODate(s.startDate);
  if (!start) return [];
  const bars: Bar[] = [];
  bars.push({ step: 'Order Started', label: 'Order Start', color: 'var(--mantine-color-orange-6)', start, end: start, milestone: true });
  const supDays = s.supplyDays ?? 0;
  const supEnd = addWorkdays(start, supDays);
  bars.push({ step: 'Supplying Consumables', label: 'Supplying Consumables', color: 'var(--mantine-color-gray-6)', start, end: supEnd, milestone: supDays === 0 });
  const exUser = fromISODate(s.executionStartDate);
  const exStart = exUser && exUser > supEnd ? exUser : supEnd;
  const exDays = s.executionDays ?? 0;
  const exEnd = addWorkdays(exStart, exDays);
  bars.push({ step: 'In Execution', label: 'In Execution', color: 'var(--mantine-color-orange-6)', start: exStart, end: exEnd, milestone: exDays === 0 });
  const prepUser = fromISODate(s.prepStartDate);
  const prepStart = prepUser && prepUser > exEnd ? prepUser : exEnd;
  const prepDays = s.prepDays ?? 0;
  const prepEnd = addWorkdays(prepStart, prepDays);
  bars.push({ step: 'Preparing to Ship', label: 'Preparing to Ship', color: 'var(--mantine-color-blue-6)', start: prepStart, end: prepEnd, milestone: prepDays === 0 });
  const shipUser = fromISODate(s.shippingStartDate);
  const shipStart = shipUser && shipUser > prepEnd ? shipUser : prepEnd;
  const shipDays = s.shippingDays ?? 0;
  const shipEnd = addWorkdays(shipStart, shipDays);
  bars.push({ step: 'Shipped', label: 'Shipping', color: 'var(--mantine-color-cyan-6)', start: shipStart, end: shipEnd, milestone: shipDays === 0 });
  // Delivered is a 0-day milestone pinned to shipping end; only surfaced in the Ongoing
  // Order view so the Quotation edit modal's 5-bar chart is unchanged.
  if (includeDelivered) {
    bars.push({ step: 'Delivered', label: 'Delivered', color: 'var(--mantine-color-green-6)', start: shipEnd, end: shipEnd, milestone: true });
  }
  return bars;
}

export default function OrderGanttSection({
  schedule,
  currentStep,
  technicalDifficulty,
  showToday,
  defaultOpen,
  includeDelivered,
}: {
  schedule: QuotationSchedule;
  // ORDER_STEPS literal — highlights the matching Gantt row with an orange outline.
  currentStep?: 'Order Started' | 'Supplying Consumables' | 'In Execution' | 'Preparing to Ship' | 'Shipped' | 'Delivered' | null;
  // Recolours the currentStep bar in red when set (matches the Ongoing screen's TD flag).
  technicalDifficulty?: boolean;
  // Draws a vertical dashed red line at today's date. Ongoing Order view flips this on.
  showToday?: boolean;
  // Ongoing Order view wants the Gantt expanded by default.
  defaultOpen?: boolean;
  // Appends a Delivered milestone at the end of shipping. Only enabled by the Ongoing view.
  includeDelivered?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const bars = useMemo(() => computeBars(schedule, !!includeDelivered), [schedule, includeDelivered]);

  const chart = useMemo(() => {
    if (bars.length === 0) return null;
    const first = bars.reduce((m, b) => (b.start < m ? b.start : m), bars[0].start);
    const last = bars.reduce((m, b) => (b.end > m ? b.end : m), bars[0].end);
    const totalDays = Math.max(1, calendarDaysBetween(first, last));
    // Pick a pixel scale that keeps the chart ≤ ~900 px wide.
    const PX_PER_DAY = Math.max(8, Math.min(48, Math.floor(900 / totalDays)));
    const rowHeight = 30;
    const leftGutter = 180;
    const topGutter = 36;
    const widthPx = leftGutter + totalDays * PX_PER_DAY + 40;
    const heightPx = topGutter + bars.length * rowHeight + 20;
    const xFor = (d: Date) => leftGutter + calendarDaysBetween(first, d) * PX_PER_DAY;
    const yFor = (i: number) => topGutter + i * rowHeight + rowHeight / 2;

    // Axis ticks — one per week (or daily if < 14 days).
    const tickStep = totalDays <= 14 ? 1 : totalDays <= 60 ? 7 : 14;
    const ticks: Array<{ x: number; label: string }> = [];
    for (let i = 0; i <= totalDays; i += tickStep) {
      const tickDate = new Date(first);
      tickDate.setDate(tickDate.getDate() + i);
      ticks.push({ x: leftGutter + i * PX_PER_DAY, label: dayjs(tickDate).format('MMM D') });
    }

    return { bars, first, last, totalDays, PX_PER_DAY, rowHeight, leftGutter, topGutter, widthPx, heightPx, xFor, yFor, ticks };
  }, [bars]);

  return (
    <div className="section">
      <div className="section-header" style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <Group gap={8} wrap="nowrap"><IconGantt size={16} /><span>Order GANTT {open ? '▾' : '▸'}</span></Group>
      </div>
      <Collapse in={open}>
        <div className="section-body">
          {!chart ? (
            <Text c="dimmed" size="sm" className="section-stub">
              Fill in at least an Order Start in the Order Duration table above to see the gantt.
            </Text>
          ) : (
            <Stack gap={6}>
              <div style={{ overflowX: 'auto' }}>
                <svg
                  width={chart.widthPx}
                  height={chart.heightPx}
                  style={{ display: 'block', fontFamily: 'inherit', fontSize: 12 }}
                >
                  {/* Weekend column shading — rendered first so everything else sits on top. */}
                  {(() => {
                    const rects: React.ReactElement[] = [];
                    const plotTop = chart.topGutter - 14;
                    const plotBottom = chart.heightPx - 10;
                    for (let i = 0; i < chart.totalDays; i++) {
                      const d = new Date(chart.first);
                      d.setDate(d.getDate() + i);
                      const w = d.getDay();
                      if (w === 0 || w === 6) {
                        rects.push(
                          <rect
                            key={`wk${i}`}
                            x={chart.leftGutter + i * chart.PX_PER_DAY}
                            y={plotTop}
                            width={chart.PX_PER_DAY}
                            height={plotBottom - plotTop}
                            fill="currentColor"
                            opacity="0.07"
                          />
                        );
                      }
                    }
                    return rects;
                  })()}
                  {/* Thin vertical gridline per calendar day. */}
                  {Array.from({ length: chart.totalDays + 1 }).map((_, i) => (
                    <line
                      key={`g${i}`}
                      x1={chart.leftGutter + i * chart.PX_PER_DAY}
                      y1={chart.topGutter - 14}
                      x2={chart.leftGutter + i * chart.PX_PER_DAY}
                      y2={chart.heightPx - 10}
                      stroke="currentColor"
                      strokeOpacity="0.08"
                    />
                  ))}
                  {/* Axis line */}
                  <line
                    x1={chart.leftGutter}
                    y1={chart.topGutter - 14}
                    x2={chart.leftGutter + chart.totalDays * chart.PX_PER_DAY}
                    y2={chart.topGutter - 14}
                    stroke="currentColor"
                    strokeOpacity="0.35"
                  />
                  {/* Axis ticks */}
                  {chart.ticks.map((t, i) => (
                    <g key={i}>
                      <line x1={t.x} y1={chart.topGutter - 18} x2={t.x} y2={chart.topGutter - 10} stroke="currentColor" strokeOpacity="0.45" />
                      <text x={t.x} y={chart.topGutter - 22} textAnchor="middle" fill="currentColor" opacity="0.7">
                        {t.label}
                      </text>
                    </g>
                  ))}
                  {/* Dependency connectors (simple L from previous bar's end to next bar's start) */}
                  {chart.bars.slice(1).map((b, i) => {
                    const prev = chart.bars[i];
                    const x1 = chart.xFor(prev.end);
                    const y1 = chart.yFor(i);
                    const x2 = chart.xFor(b.start);
                    const y2 = chart.yFor(i + 1);
                    const midX = Math.max(x1 + 6, x2 - 6);
                    return (
                      <polyline
                        key={`c${i}`}
                        points={`${x1},${y1} ${midX},${y1} ${midX},${y2} ${x2 - 4},${y2}`}
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity="0.4"
                        strokeDasharray="3 3"
                      />
                    );
                  })}
                  {/* Arrowheads at the end of each connector */}
                  {chart.bars.slice(1).map((b, i) => {
                    const x2 = chart.xFor(b.start);
                    const y2 = chart.yFor(i + 1);
                    return (
                      <polygon
                        key={`a${i}`}
                        points={`${x2 - 4},${y2 - 3} ${x2},${y2} ${x2 - 4},${y2 + 3}`}
                        fill="currentColor"
                        opacity="0.6"
                      />
                    );
                  })}
                  {/* Today marker — a dashed red line drawn behind the bars. Only when the
                      date falls inside the chart range so it doesn't visually push off-axis. */}
                  {showToday && (() => {
                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    if (today < chart.first || today > chart.last) return null;
                    const x = chart.xFor(today);
                    return (
                      <g>
                        <line
                          x1={x}
                          y1={chart.topGutter - 14}
                          x2={x}
                          y2={chart.heightPx - 10}
                          stroke="var(--mantine-color-red-6)"
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          opacity="0.85"
                        />
                        <text
                          x={x}
                          y={chart.heightPx - 2}
                          textAnchor="middle"
                          fill="var(--mantine-color-red-6)"
                          opacity="0.85"
                          fontSize={10}
                        >
                          Today
                        </text>
                      </g>
                    );
                  })()}
                  {/* Row labels + bars / milestones */}
                  {chart.bars.map((b, i) => {
                    const y = chart.yFor(i);
                    const x1 = chart.xFor(b.start);
                    const x2 = chart.xFor(b.end);
                    const isCurrent = currentStep === b.step;
                    // Red override when TD is stuck on this step; otherwise keep the bar's own colour.
                    const fillColor = isCurrent && technicalDifficulty ? 'var(--mantine-color-red-6)' : b.color;
                    // Brighten + thicken the stroke on the current step so it reads as "you are here".
                    const highlightStroke = isCurrent
                      ? (technicalDifficulty ? 'var(--mantine-color-red-8)' : 'var(--mantine-color-orange-8)')
                      : 'currentColor';
                    const highlightStrokeWidth = isCurrent ? 2 : 1;
                    const highlightStrokeOpacity = isCurrent ? 1 : 0.4;
                    return (
                      <g key={i}>
                        <text
                          x={chart.leftGutter - 8}
                          y={y + 4}
                          textAnchor="end"
                          fill={isCurrent ? (technicalDifficulty ? 'var(--mantine-color-red-7)' : 'var(--mantine-color-orange-7)') : 'currentColor'}
                          opacity={isCurrent ? 1 : 0.85}
                          fontWeight={isCurrent ? 600 : 400}
                        >
                          {b.label}
                        </text>
                        {b.milestone ? (
                          <polygon
                            points={`${x1},${y - 7} ${x1 + 7},${y} ${x1},${y + 7} ${x1 - 7},${y}`}
                            fill={fillColor}
                            stroke={highlightStroke}
                            strokeWidth={highlightStrokeWidth}
                            strokeOpacity={highlightStrokeOpacity}
                          />
                        ) : (
                          <rect
                            x={x1}
                            y={y - 8}
                            width={Math.max(2, x2 - x1)}
                            height={16}
                            fill={fillColor}
                            opacity={isCurrent ? 1 : 0.85}
                            rx={2}
                            stroke={highlightStroke}
                            strokeWidth={highlightStrokeWidth}
                            strokeOpacity={highlightStrokeOpacity}
                          />
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
              <Text size="xs" c="dimmed">
                Milestones are diamonds (0-day steps). Bars span the calendar days the step covers. Arrows show finish-to-start dependencies. Shaded columns are weekends (Sat/Sun).
              </Text>
            </Stack>
          )}
        </div>
      </Collapse>
    </div>
  );
}
